import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useStealth } from "../context/StealthContext";
import { stealthCounts, stealthWipeAll } from "../lib/stealthDb";
import { safeLocal } from "../lib/safeStorage";
import { STEALTH_ENTRY_URL_KEY } from "../lib/storageKeys";

/**
 * Routes we never want to bounce a user back to after they exit
 * Stealth Mode — even if the entry URL recorded one. The exit flow
 * implies they wanted to leave anonymous mode behind, so landing
 * them on /onboarding or /capture would just send them right back
 * into anonymous-only territory.
 */
const STEALTH_ONLY_EXIT_DENYLIST = [
  "/onboarding",
  "/capture",
  "/analyzing",
  "/auth/", // any /auth/callback flavour
];

/**
 * @param {string} candidate - the URL we'd like to redirect to on exit
 * @param {string} currentPath - where the user is RIGHT NOW
 * @returns {string|null} the URL to navigate to, or null to stay put
 */
const resolveExitUrl = (candidate, currentPath) => {
  if (!candidate || typeof candidate !== "string") return null;
  if (!candidate.startsWith("/")) return null; // open-redirect guard
  if (candidate === currentPath) return null; // would be a no-op
  if (STEALTH_ONLY_EXIT_DENYLIST.some((p) => candidate.startsWith(p))) {
    return null;
  }
  return candidate;
};

/**
 * Shared Stealth Mode toggle logic. Used by both /settings (the
 * canonical control surface) and the header pill (the always-visible
 * escape hatch). Single source of truth for:
 *   - reading + tracking the live counts of locally-stored routines
 *     and snapshots,
 *   - confirming destructive transitions (Stealth ON → OFF wipes the
 *     local-only data so a future leak can't expose it),
 *   - remembering the entry URL so an admin who toggled stealth on
 *     while sitting on /admin gets bounced right back there on exit,
 *   - emitting a single, locale-aware toast.
 */
export const useStealthToggle = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { stealth, setStealth } = useStealth();
  const [counts, setCounts] = useState({ routines: 0, snapshots: 0 });

  // Keep counts fresh whenever the toggle flips — IndexedDB writes
  // are async so we need to re-read after each state change.
  const refreshCounts = useCallback(() => {
    stealthCounts().then(setCounts).catch(() => {});
  }, []);

  useEffect(() => {
    refreshCounts();
  }, [stealth, refreshCounts]);

  const toggle = useCallback(
    /**
     * @param {boolean} next - desired stealth state (true = on)
     * @param {{ skipConfirm?: boolean, entryUrl?: string }} [opts]
     *   `entryUrl` overrides "current pathname" when turning ON —
     *   used by `useGuestStart` so the auto-enabled stealth from
     *   "Skip sign-up" remembers the landing URL, not /onboarding.
     * @returns {Promise<boolean>} true if the toggle happened, false if aborted
     */
    async (next, opts = {}) => {
      if (next === stealth) return false;

      if (next) {
        // Turning ON: remember where the user is so we can restore
        // them on the way out. We capture pathname + search so deep
        // links (e.g. /admin?tab=revenue) survive the round-trip.
        const here =
          opts.entryUrl ||
          (typeof window !== "undefined"
            ? (window.location.pathname || "") + (window.location.search || "")
            : "");
        if (here) {
          safeLocal.setItem(STEALTH_ENTRY_URL_KEY, here);
        }
      } else {
        // Turning OFF means we're about to start syncing again. Any
        // local-only routines / snapshots would otherwise sit stranded
        // in IndexedDB on this device. Confirm + wipe.
        if (counts.routines > 0 || counts.snapshots > 0) {
          if (!opts.skipConfirm) {
            const ok = window.confirm(
              t("stealth.confirmWipe", {
                routines: counts.routines,
                snapshots: counts.snapshots,
              }),
            );
            if (!ok) return false;
          }
          try {
            await stealthWipeAll();
            setCounts({ routines: 0, snapshots: 0 });
          } catch (e) {
            if (process.env.NODE_ENV !== "production") {
              // eslint-disable-next-line no-console
              console.debug("[stealth-toggle] wipe failed:", e?.message || e);
            }
            toast.error(t("stealth.wipeFailed"));
            return false;
          }
        }
      }

      setStealth(next);

      // Hand the user back to where they were when they entered
      // stealth, if we have a usable URL on record.
      if (!next) {
        const saved = safeLocal.getItem(STEALTH_ENTRY_URL_KEY);
        safeLocal.removeItem(STEALTH_ENTRY_URL_KEY);
        const currentPath =
          typeof window !== "undefined" ? window.location.pathname : "";
        const target = resolveExitUrl(saved, currentPath);
        if (target) {
          navigate(target);
        }
      }

      toast.success(next ? t("stealth.toastOn") : t("stealth.toastOff"));
      return true;
    },
    [stealth, counts, setStealth, t, navigate],
  );

  return { stealth, setStealth, counts, toggle, refreshCounts };
};

export default useStealthToggle;
